import _ from 'underscore'

import React, { useCallback, useEffect, useState } from 'react'

import styled from 'styled-components'

import Modal from 'components/Modal'
import ModalButton from 'components/Modal/Button'
import TextInput from 'components/TextInput'
import Spinner from 'components/Spinner'
import Feature from 'components/UserInfoMenu/FeaturesManager/Feature'

import { useSessionContext } from 'hooks/SessionContext'
import useServerlessDealerFeatures from 'hooks/useServerlessDealerFeatures'

import ServerlessApi from 'services/ServerlessApi'

import { scrubText } from 'helpers/inputCleaners'

import { ButtonContainer } from 'styles/shared'

type Props = {
  closeCallback: (reloadPage: boolean) => void
}

const FeaturesModal = ({ closeCallback }: Props) => {
  const { session: { dealerId, isAdmin, isImpersonating } } = useSessionContext()
  const { features: oldEnabledFeatures, isFeaturesLoaded } = useServerlessDealerFeatures(dealerId)

  const [isAddingFeature, setIsAddingFeature] = useState(false)
  const [inputFeatureCode, setInputFeatureCode] = useState<string>('')
  const [newEnabledFeatures, setNewEnabledFeatures] = useState<Array<string>>([])
  const [note, setNote] = useState('')

  useEffect(() => {
    if (isFeaturesLoaded) {
      setNewEnabledFeatures(oldEnabledFeatures || [])
      setNote('')
    }
  }, [oldEnabledFeatures, isFeaturesLoaded])

  const handleKeyDown = async (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !event.currentTarget.readOnly) {
      await onClickEnableFeature()
    }
  }

  const onClickClose = () => {
    const reloadPage = newEnabledFeatures.length !== oldEnabledFeatures?.length || _.difference(newEnabledFeatures, oldEnabledFeatures).length > 0
    closeCallback(reloadPage)
  }

  const onClickEnableFeature = useCallback(async () => {
    if (_.isEmpty(inputFeatureCode)) {
      return
    }

    setIsAddingFeature(true)
    setNote('')

    await ServerlessApi.Features.createDealerBetaFeature(dealerId, inputFeatureCode)
      .then(() => {
        setNewEnabledFeatures(() => [...newEnabledFeatures, inputFeatureCode])
        setInputFeatureCode('')
        setNote('Feature added successfully')
      }).catch(async (error) => {
        if (error.status === 400) {
          const errorResponse = await error.response.json()
          setNote(errorResponse?.base[0].name)
        }
      }).finally(() => {
        setIsAddingFeature(false)
      })
  }, [inputFeatureCode, dealerId, newEnabledFeatures])

  const onClickRemoveFeature = useCallback((removeFeatureCode: string) => {
    return ServerlessApi.Features.deleteDealerBetaFeature(dealerId, removeFeatureCode)
      .then(() => {
        const updatedFeatures = newEnabledFeatures.filter((feature) => feature !== removeFeatureCode)
        
        setNewEnabledFeatures(updatedFeatures)
        setInputFeatureCode('')
        setNote(`Feature '${removeFeatureCode}' successfully removed`)
      })
  }, [dealerId, newEnabledFeatures])

  return (
    <Modal title="Beta Features" variant="sm" isDisplayed closeCallback={onClickClose}>
      {isFeaturesLoaded && (
        <div>
          {!_.isEmpty(newEnabledFeatures) && (
            <div>
              <S.SectionTitle>Enabled Features</S.SectionTitle>
              <S.Features>
                {newEnabledFeatures.map((feature) => <Feature key={feature} feature={feature} onRemoveFeatureCallback={onClickRemoveFeature} />)}
              </S.Features>
            </div>)
          }

          {(!isAdmin || isImpersonating) && (
            <>
              <S.Note>{note}</S.Note>
              <TextInput
                autoFocus
                readOnly={isAddingFeature}
                label="Feature Code"
                value={inputFeatureCode ?? ''}
                onChange={(event) => setInputFeatureCode(scrubText(event.target.value))}
                onKeyDown={handleKeyDown}
              />
            </>
          )}

          <ButtonContainer noBorder>
            <ModalButton decoration="secondary" text="Close" onPress={onClickClose} />
            {(!isAdmin || isImpersonating) && <ModalButton decoration="positive" text="Enable Feature" onPress={onClickEnableFeature} showSpinner={isAddingFeature} />}
          </ButtonContainer>
        </div>
      )}
      {!isFeaturesLoaded && <Spinner variant="page" />}
    </Modal>
  )
}

export default FeaturesModal

const S = {
  Features: styled.div`
    display: inline-block;
  `,
  Note: styled.div`
    margin: 5px 0px 5px 0px;
  `,
  SectionTitle: styled.h2`
    text-transform: capitalize;
  `
}
